import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "../../../stores/RootStore";
import { Plant } from "./types";

export default class PlantOverviewStore {
  // Observables
  plants: Plant[] = [];
  selectedPlantId: string | null = null;
  isLoading = false;
  error: string | null = null;
  filterStatus: "all" | "active" | "inactive" | "maintenance" = "all";
  searchQuery = "";
  timeRange: "month" | "quarter" | "year" = "year";

  // CFO-relevant metrics from various services
  outstandingInvoices = 0;
  overduePayments = 0;
  inventoryTurnoverRate = 0;
  pendingPurchaseOrders = 0;
  qualityDefectRate = 0;
  onTimeDeliveryRate = 0;
  cashFlowPosition = 0;
  operatingMargin = 0;
  workforceEfficiency = 0;
  maintenanceCosts = 0;
  energyCostPerUnit = 0;
  customerRetentionRate = 0;

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
    // Don't auto-initialize - let the route handle it
  }

  // Actions
  setSelectedPlant(plantId: string | null) {
    this.selectedPlantId = plantId;
  }

  setFilterStatus(status: "all" | "active" | "inactive" | "maintenance") {
    this.filterStatus = status;
  }

  setSearchQuery(query: string) {
    this.searchQuery = query;
  }

  setTimeRange(range: "month" | "quarter" | "year") {
    this.timeRange = range;
  }

  clearError() {
    this.error = null;
  }

  async loadCFOMetrics() {
    // Simulate loading metrics from various services (financial, manufacturing, CRM, IoT, etc.)
    runInAction(() => {
      // Financial service metrics
      this.outstandingInvoices = 3420000; // $3.42M in outstanding invoices
      this.overduePayments = 287000; // $287K overdue (>30 days)
      this.cashFlowPosition = 8750000; // $8.75M positive cash flow
      this.operatingMargin = 18.5; // 18.5% operating margin

      // Manufacturing/Inventory metrics
      this.inventoryTurnoverRate = 6.2; // 6.2x inventory turns per year
      this.pendingPurchaseOrders = 42; // 42 pending POs
      this.qualityDefectRate = 2.1; // 2.1% defect rate
      this.maintenanceCosts = 450000; // $450K monthly maintenance costs
      this.energyCostPerUnit = 12.50; // $12.50 energy cost per unit

      // Operations/Delivery metrics
      this.onTimeDeliveryRate = 94.3; // 94.3% on-time delivery
      this.workforceEfficiency = 87.5; // 87.5% workforce efficiency

      // CRM/Customer metrics
      this.customerRetentionRate = 92.0; // 92% customer retention rate
    });
  }

  // Async actions
  async initializePlants() {
    console.log("PlantOverviewStore: Starting initializePlants");

    runInAction(() => {
      this.isLoading = true;
      this.error = null;
    });

    try {
      // Generate mock data for the 7 plants
      const mockPlants: Plant[] = [
        {
          id: "1",
          name: "Summit Manufacturing",
          location: "Bridger, MT",
          manager: "David Anderson",
          status: "active",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        {
          id: "2",
          name: "Pacific Industrial Works",
          location: "Cascade, OR",
          manager: "Lisa Rodriguez",
          status: "active",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        {
          id: "3",
          name: "Northern Production Facility",
          location: "Columbia, WA",
          manager: "James Peterson",
          status: "active",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        {
          id: "4",
          name: "Bay Area Operations",
          location: "San Francisco, CA",
          manager: "Amanda Foster",
          status: "active",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        {
          id: "5",
          name: "Gulf Coast Industries",
          location: "Houston, TX",
          manager: "Thomas Mitchell",
          status: "active",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        {
          id: "6",
          name: "Western Materials Plant",
          location: "Medford, OR",
          manager: "Rachel Stevens",
          status: "active",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        {
          id: "7",
          name: "Desert Manufacturing Hub",
          location: "Phoenix, AZ",
          manager: "Michael Harrison",
          status: "maintenance",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      ];

      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Load CFO metrics from various services
      await this.loadCFOMetrics();

      console.log("PlantOverviewStore: Setting plants data", mockPlants.length);

      runInAction(() => {
        this.plants = mockPlants;
        this.isLoading = false;
      });

      console.log("PlantOverviewStore: Plants initialized successfully");
    } catch (error) {
      console.error("PlantOverviewStore: Error initializing plants", error);
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to load plants";
        this.isLoading = false;
      });
    }
  }

  async refreshPlantMetrics() {
    if (!this.plants.length) {
      await this.initializePlants();
      return;
    }

    runInAction(() => {
      this.isLoading = true;
    });

    try {
      // Simulate refreshing metrics with slightly varied data
      const updatedPlants = this.plants.map((plant) => ({
        ...plant,
        updatedAt: new Date().toISOString(),
      }));

      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 300));

      runInAction(() => {
        this.plants = updatedPlants;
        this.isLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to refresh metrics";
        this.isLoading = false;
      });
    }
  }

  navigateToPlant(plantId: string) {
    // Navigate to specific plant dashboard
    this.rootStore.routerStore.goTo("PlantDashboard", { plantId });
  }

  // Computed values are below, plants is now an observable array

  get filteredPlants(): Plant[] {
    let filtered = this.plants;

    // Filter by status
    if (this.filterStatus !== "all") {
      filtered = filtered.filter((plant) => plant.status === this.filterStatus);
    }

    // Filter by search query
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(
        (plant) =>
          plant.name.toLowerCase().includes(query) ||
          plant.location?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }

  get selectedPlant(): Plant | null {
    if (!this.selectedPlantId) return null;
    return (
      this.plants.find((plant) => plant.id === this.selectedPlantId) || null
    );
  }

  get totalMonthRevenue(): number {
    // Mock value for demo
    return this.filteredPlants.length * 500000;
  }

  get totalYearRevenue(): number {
    // Mock value for demo
    return this.filteredPlants.length * 6000000;
  }

  get totalMonthProfit(): number {
    // Mock value for demo
    return this.filteredPlants.length * 100000;
  }

  get totalYearProfit(): number {
    // Mock value for demo
    return this.filteredPlants.length * 1200000;
  }

  get activePlantCount(): number {
    return this.plants.filter((plant) => plant.status === "active").length;
  }

  get totalEmployeeCount(): number {
    // Mock value for demo
    return this.plants.length * 25;
  }

  get averageUtilization(): number {
    const activePlants = this.plants.filter(
      (plant) => plant.status === "active"
    );
    if (activePlants.length === 0) return 0;

    // Mock utilization rate for demo
    return 75;
  }
}
